/** @module A variant's Cardmarket value: figure, change, history bars and the fields behind it. */
import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type components, problemFrom } from "@/api/client";
import { BarChart, sample } from "@/components/ui/bar-chart";
import { Segmented } from "@/components/ui/controls";
import { dateTime, dayMonth, money } from "@/lib/format";

type Variant = components["schemas"]["CardVariantOut"];
type Price = components["schemas"]["PriceOut"];
type Range = "30d" | "90d" | "1y" | "all";

const RANGES: Range[] = ["30d", "90d", "1y", "all"];

async function fetchHistory(printId: string, variantId: number, range: Range) {
  const { data, response } = await api.GET("/api/v1/cards/{print_id}/prices", {
    params: { path: { print_id: printId }, query: { variant_id: variantId, range } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

export function marketPrice(variant: Variant | undefined, market: string): Price | undefined {
  return (variant?.prices ?? []).find((price) => price.market === market);
}

function Change({ percent }: { percent: number }) {
  const { t, i18n } = useTranslation();
  const up = percent >= 0;
  return (
    <span className={`badge ${up ? "badge-gain" : "badge-loss"}`}>
      {up ? <ArrowUpRight /> : <ArrowDownRight />}
      {up ? "+" : "−"}
      {Math.abs(percent).toLocaleString(i18n.resolvedLanguage ?? "de", {
        maximumFractionDigits: 1,
      })}{" "}
      % · {t("dashboard.rangeShort.7d")}
    </span>
  );
}

/** Which fields the value could have come from, and which one it did. */
function Fields({ price }: { price: Price }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const chain = price.chain ?? [];
  if (chain.length === 0) return null;
  return (
    <table className="t-sm hidden w-full md:table">
      <thead>
        <tr className="border-b border-(--border)">
          <th className="t-label px-[10px] py-2 text-left">{t("price.field")}</th>
          <th className="t-label px-[10px] py-2 text-right">{t("price.raw")}</th>
          <th className="t-label px-[10px] py-2 text-left">{t("price.status")}</th>
        </tr>
      </thead>
      <tbody>
        {chain.map((field) => {
          const zero = field.raw !== null && Number(field.raw) === 0;
          return (
            <tr
              key={field.name}
              className={`border-b border-(--border) ${field.used ? "bg-(--accent-soft) font-semibold" : ""}`}
            >
              <td className="num px-[10px] py-2">{field.name}</td>
              <td className="num px-[10px] py-2 text-right">
                {field.raw === null ? "–" : (money(field.raw, locale) ?? "0,00")}
              </td>
              <td className={`px-[10px] py-2 ${field.used ? "" : "t-muted"}`}>
                {field.used ? t("price.used") : zero ? t("price.zeroMissing") : ""}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function PriceBox({ printId, variant }: { printId: string; variant: Variant }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const [range, setRange] = useState<Range>("90d");
  const price = marketPrice(variant, "cardmarket");
  const tcgplayer = marketPrice(variant, "tcgplayer");
  const history = useQuery({
    queryKey: ["card-prices", printId, variant.id, range],
    queryFn: () => fetchHistory(printId, variant.id, range),
  });

  // Suspicious points stay out of the bars: one wrong product id would dwarf the rest.
  const points = sample(
    (history.data?.points ?? []).filter((point) => point.value !== null && !point.suspicious),
  );
  const first = points[0];
  const middle = points[Math.floor(points.length / 2)];

  let figure;
  if (!price?.value) {
    figure = <span className="num-xl t-muted">{t("collection.unvalued")}</span>;
  } else if (price.suspicious) {
    figure = (
      <>
        <span className="num-xl t-muted line-through decoration-2">
          {money(price.value, locale)}
        </span>
        <span className="badge badge-warn">
          <TriangleAlert />
          {t("collection.suspicious")}
        </span>
      </>
    );
  } else {
    figure = (
      <>
        <span className="num-xl">{money(price.value, locale)}</span>
        {price.change_7d !== null && price.change_7d !== undefined ? (
          <Change percent={price.change_7d} />
        ) : null}
      </>
    );
  }

  return (
    <section className="panel panel-lg flex flex-col gap-[14px]">
      <div className="row-between">
        <span className="t-label">
          {t("price.heading", { family: t(`price.family.${price?.family ?? "base"}`) })}
        </span>
        <span className="hidden md:block">
          <Segmented
            label={t("catalog.priceHistory")}
            value={range}
            onChange={setRange}
            options={RANGES.map((option) => ({
              value: option,
              label: t(`dashboard.rangeShort.${option}`),
            }))}
          />
        </span>
      </div>
      <div className="row flex-wrap items-baseline gap-3">
        {figure}
        {price?.value_field ? (
          <span className="badge badge-info">{t("price.from", { field: price.value_field })}</span>
        ) : null}
      </div>
      {points.length < 2 ? (
        <p className="t-sm t-muted">
          {history.isPending ? t("common.loading") : t("catalog.noHistory")}
        </p>
      ) : (
        <BarChart
          className="h-14 md:h-[110px]"
          label={t("price.historyLabel", {
            from: money(first?.value, locale),
            to: money(points[points.length - 1]?.value, locale),
          })}
          bars={points.map((point) => ({
            key: point.captured_on,
            value: Number(point.value),
            title: `${dayMonth(point.captured_on, locale)}: ${money(point.value, locale) ?? "–"}`,
          }))}
          axis={[
            first ? dayMonth(first.captured_on, locale) : "",
            middle ? dayMonth(middle.captured_on, locale) : "",
            t("dashboard.today"),
          ]}
        />
      )}
      {price ? <Fields price={price} /> : null}
      <div className="row-between t-xs t-muted flex-wrap gap-2">
        <span>
          {tcgplayer?.value
            ? t("price.tcgplayer", {
                value: new Intl.NumberFormat(locale, {
                  style: "currency",
                  currency: tcgplayer.currency,
                }).format(Number(tcgplayer.value)),
              })
            : null}
        </span>
        {price?.updated_at ? (
          <span>{t("price.updated", { when: dateTime(price.updated_at, locale) })}</span>
        ) : null}
      </div>
    </section>
  );
}
